import { PageLoader } from '@/components/ui/loader';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { CheckCircle2, Clock, Eraser, FileText, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Textarea } from '@/components/ui/misc';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { ApiSuccess } from '@/lib/types';

type SigningView = {
  signer: { name: string; email: string; signed: boolean; declined: boolean };
  request: {
    title: string;
    message: string | null;
    status: string;
    documentName: string;
    documentHash: string | null;
    expiresAt: string | null;
  };
  organizationName: string;
  yourTurn: boolean;
  consentText: string;
  file: { url: string; mimeType: string; name: string } | null;
};

/**
 * The signing page (spec DOC-11).
 *
 * Outside the app and outside auth: the token in the link is the credential.
 * The signature is drawn on a canvas and sent as an image, alongside the tick
 * that records agreement to the wording shown — the server stores that wording
 * with the signature, so changing it later cannot rewrite what was agreed.
 */
export function PublicSignPage() {
  const { token } = useParams<{ token: string }>();
  const queryClient = useQueryClient();
  const base = import.meta.env.VITE_API_URL ?? '/api/v1';

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ['signing', token],
    queryFn: async () => {
      const response = await axios.get<ApiSuccess<SigningView>>(`${base}/public/sign/${token}`);
      return response.data.data;
    },
    retry: false,
  });

  const sign = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const response = await axios.post<ApiSuccess<{ signed: boolean; everyoneSigned: boolean }>>(
        `${base}/public/sign/${token}`,
        body,
      );
      return response.data.data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['signing', token] }),
  });

  const decline = useMutation({
    mutationFn: async (reason: string) => {
      const response = await axios.post<ApiSuccess<{ declined: boolean }>>(
        `${base}/public/sign/${token}/decline`,
        { reason },
      );
      return response.data.data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['signing', token] }),
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [hasMark, setHasMark] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data && !typedName) setTypedName(data.signer.name);
  }, [data, typedName]);

  if (isLoading) return <Shell><PageLoader /></Shell>;

  if (loadError || !data) {
    const message = axios.isAxiosError(loadError)
      ? ((loadError.response?.data as { error?: { message?: string } })?.error?.message ??
        'This signing link is not valid')
      : 'This signing link is not valid';

    return (
      <Shell>
        <div className="text-center">
          <XCircle className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <h1 className="text-lg font-semibold">{message}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Please ask your contact to send a new one.
          </p>
        </div>
      </Shell>
    );
  }

  if (data.signer.signed) {
    return (
      <Shell>
        <div className="text-center">
          <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
          <h1 className="text-lg font-semibold">You have signed this</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {data.request.title} — {data.organizationName} has been told.
          </p>
          {data.request.documentHash && <Hash hash={data.request.documentHash} />}
        </div>
      </Shell>
    );
  }

  if (data.signer.declined) {
    return (
      <Shell>
        <div className="text-center">
          <XCircle className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <h1 className="text-lg font-semibold">You declined this request</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {data.organizationName} has been told.
          </p>
        </div>
      </Shell>
    );
  }

  if (!data.yourTurn) {
    return (
      <Shell>
        <div className="text-center">
          <Clock className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <h1 className="text-lg font-semibold">Not your turn yet</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            This document is being signed in order. You will be emailed when it reaches you.
          </p>
        </div>
      </Shell>
    );
  }

  const clear = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (canvas && context) context.clearRect(0, 0, canvas.width, canvas.height);
    setHasMark(false);
  };

  const pointAt = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const box = canvas.getBoundingClientRect();
    // The canvas is drawn at twice its CSS size so the mark stays crisp.
    return {
      x: ((event.clientX - box.left) / box.width) * canvas.width,
      y: ((event.clientY - box.top) / box.height) * canvas.height,
    };
  };

  return (
    <Shell wide>
      <div className="mb-6 text-center">
        <FileText className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
        <h1 className="text-xl font-semibold">{data.request.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {data.organizationName} has asked you to sign {data.request.documentName}
        </p>
        {data.request.expiresAt && (
          <p className="mt-1 text-xs text-muted-foreground">
            This request expires on {formatDate(data.request.expiresAt)}
          </p>
        )}
      </div>

      {data.request.message && (
        <p className="mb-5 rounded-lg border bg-muted/40 p-4 text-sm">{data.request.message}</p>
      )}

      {data.file && (
        <div className="mb-6">
          {data.file.mimeType === 'application/pdf' || data.file.mimeType.startsWith('image/') ? (
            <iframe
              src={data.file.url}
              title={data.file.name}
              className="h-[26rem] w-full rounded-lg border"
            />
          ) : (
            <div className="rounded-lg border p-4 text-center text-sm">
              <a href={data.file.url} target="_blank" rel="noopener" className="text-link hover:underline">
                Open {data.file.name}
              </a>
              <p className="mt-1 text-xs text-muted-foreground">
                Read the document before you sign it.
              </p>
            </div>
          )}
        </div>
      )}

      {declining ? (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Why are you declining?</Label>
            <Textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} />
          </div>
          <div className="flex gap-2">
            <Button
              variant="destructive"
              disabled={reason.trim().length < 3 || decline.isPending}
              onClick={() => decline.mutate(reason)}
            >
              Decline to sign
            </Button>
            <Button variant="outline" onClick={() => setDeclining(false)}>
              Back
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label>Your name</Label>
            <Input value={typedName} onChange={(event) => setTypedName(event.target.value)} />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Draw your signature</Label>
              <button
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                onClick={clear}
              >
                <Eraser className="h-3 w-3" />
                Clear
              </button>
            </div>
            <canvas
              ref={canvasRef}
              width={1000}
              height={280}
              className={cn(
                'h-36 w-full touch-none rounded-lg border-2 border-dashed bg-white',
                hasMark && 'border-solid border-primary/40',
              )}
              onPointerDown={(event) => {
                const point = pointAt(event);
                const context = canvasRef.current?.getContext('2d');
                if (!point || !context) return;
                event.currentTarget.setPointerCapture(event.pointerId);
                context.strokeStyle = '#0f172a';
                context.lineWidth = 3;
                context.lineCap = 'round';
                context.lineJoin = 'round';
                context.beginPath();
                context.moveTo(point.x, point.y);
                setDrawing(true);
                setHasMark(true);
              }}
              onPointerMove={(event) => {
                if (!drawing) return;
                const point = pointAt(event);
                const context = canvasRef.current?.getContext('2d');
                if (!point || !context) return;
                context.lineTo(point.x, point.y);
                context.stroke();
              }}
              onPointerUp={() => setDrawing(false)}
              onPointerLeave={() => setDrawing(false)}
            />
            {!hasMark && (
              <p className="text-xs text-muted-foreground">
                Sign with a mouse, a finger or a stylus.
              </p>
            )}
          </div>

          <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border p-4">
            <Checkbox checked={agreed} onCheckedChange={(value) => setAgreed(value === true)} />
            <span className="text-sm leading-relaxed">{data.consentText}</span>
          </label>

          {error && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              size="lg"
              disabled={!agreed || !hasMark || typedName.trim().length < 2 || sign.isPending}
              onClick={async () => {
                setError(null);
                try {
                  await sign.mutateAsync({
                    signatureImage: canvasRef.current?.toDataURL('image/png'),
                    typedName,
                    agreed,
                  });
                } catch (caught) {
                  setError(
                    axios.isAxiosError(caught)
                      ? ((caught.response?.data as { error?: { message?: string } })?.error
                          ?.message ?? 'Could not record your signature.')
                      : 'Could not record your signature.',
                  );
                }
              }}
            >
              {sign.isPending ? 'Signing…' : 'Sign document'}
            </Button>
            <Button variant="outline" onClick={() => setDeclining(true)}>
              Decline
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            Your name, the time, your IP address and this wording are recorded with your
            signature, and appear on the certificate.
          </p>
        </div>
      )}
    </Shell>
  );
}

function Hash({ hash }: { hash: string }) {
  return (
    <div className="mt-5 rounded-lg border bg-muted/40 p-3 text-start">
      <div className="text-xs text-muted-foreground">SHA-256 of the document you signed</div>
      <code className="mt-0.5 block break-all text-[11px]">{hash}</code>
    </div>
  );
}

function Shell({ children, wide }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 px-4 py-10">
      <div
        className={cn(
          'w-full rounded-lg border bg-background p-6 shadow-sm sm:p-8',
          wide ? 'max-w-2xl' : 'max-w-md',
        )}
      >
        {children}
      </div>
    </div>
  );
}
